import { FINANCIAL_FORECAST_REDUCER_KEY } from "../index";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import { defaultZeroMonths } from "./staticMonthsData";
import { GeneralCoType } from "constants/companyTypes";

import * as actionTypes from "../../actions/actionTypes";

export const UNIT_SALES_REDUCER_KEY = "unitSales";
export const QUANTITY_KEY = "quantity";
export const PRICE_KEY = "price";
export const INCOME_FROM_SALES_KEY = "incomeFromSales";
export const COST_PRICE = "costPrice";
export const COST_OF_SALES = "costOfSales";
export const UNIT_SALES_INSTANCES_KEY = "unitSalesInstances";
export const UNIT_SALES_TOTAL_INCOME_KEY = "unitSalesTotalIncome";
export const TOTAL_COST_OF_UNIT_SALES_KEY = "totalCostOfUnitSales";

export const unitSalesInstanceItems = [
  QUANTITY_KEY,
  PRICE_KEY,
  INCOME_FROM_SALES_KEY,
  COST_PRICE,
  COST_OF_SALES,
];

export const unitSalesTotalIncomeItems = [
  UNIT_SALES_TOTAL_INCOME_KEY,
  TOTAL_COST_OF_UNIT_SALES_KEY,
];

const shouldUseMonthly = (getReducerFunc) => {
  if (!getReducerFunc) {
    return false;
  }
  try {
    const forecastState = getReducerFunc(FINANCIAL_FORECAST_REDUCER_KEY);
    return (
      forecastState?.timeSeriesAssumptions?.primaryPeriodicity === "Monthly"
    );
  } catch (err) {
    return false;
  }
};

const getPeriodValue = (item, key, getReducerFunc) => {
  if (!item) {
    return 0;
  }
  if (shouldUseMonthly(getReducerFunc)) {
    const monthKey = item.months?.[key] ?? item.months?.[String(key)];
    if (monthKey !== undefined) {
      return Number(monthKey) || 0;
    }
  }
  return Number(item.years?.[key] || 0);
};

export const unitSalesInstanceState = {
  [QUANTITY_KEY]: {
    title: "Quantity",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    allowDecimals: false,
    stripLeadingZeros: true,
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [PRICE_KEY]: {
    title: "Price",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    decimalPrecision: 4,
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [INCOME_FROM_SALES_KEY]: {
    title: "Income from Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      const instance = getReducerFunc(UNIT_SALES_REDUCER_KEY)[
        UNIT_SALES_INSTANCES_KEY
      ][instanceId];
      const quantity = getPeriodValue(
        instance[QUANTITY_KEY],
        yearKey,
        getReducerFunc
      );
      const price = getPeriodValue(
        instance[PRICE_KEY],
        yearKey,
        getReducerFunc
      );
      return quantity * price;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_PRICE]: {
    title: "Cost Price",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_OF_SALES]: {
    title: "Cost of Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      const instance = getReducerFunc(UNIT_SALES_REDUCER_KEY)[
        UNIT_SALES_INSTANCES_KEY
      ][instanceId];
      const quantity = getPeriodValue(
        instance[QUANTITY_KEY],
        yearKey,
        getReducerFunc
      );
      const costPrice = getPeriodValue(
        instance[COST_PRICE],
        yearKey,
        getReducerFunc
      );
      return quantity * costPrice;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const initialState = {
  [UNIT_SALES_INSTANCES_KEY]: {},
  [UNIT_SALES_TOTAL_INCOME_KEY]: {
    title: "Income from Unit Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    // Now we handle each year separately by calling incomeData's calculate for that year
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const unitSalesState = getReducerFunc(UNIT_SALES_REDUCER_KEY);
      const instances = unitSalesState[UNIT_SALES_INSTANCES_KEY];

      let total = 0;
      Object.values(instances).forEach((instanceData) => {
        const quantity = getPeriodValue(
          instanceData[QUANTITY_KEY],
          yearKey,
          getReducerFunc
        );
        const price = getPeriodValue(
          instanceData[PRICE_KEY],
          yearKey,
          getReducerFunc
        );
        total += quantity * price;
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_COST_OF_UNIT_SALES_KEY]: {
    title: "Cost of Unit Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    // Now we handle each year separately by calling incomeData's calculate for that year
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const unitSalesState = getReducerFunc(UNIT_SALES_REDUCER_KEY);
      const instances = unitSalesState[UNIT_SALES_INSTANCES_KEY];

      let total = 0;
      Object.values(instances).forEach((instanceData) => {
        const quantity = getPeriodValue(
          instanceData[QUANTITY_KEY],
          yearKey,
          getReducerFunc
        );
        const costPrice = getPeriodValue(
          instanceData[COST_PRICE],
          yearKey,
          getReducerFunc
        );
        total += quantity * costPrice;
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// Default instance structure
export const defaultUnitSalesState = {
  ...unitSalesInstanceState,
};

const unitSalesReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ADD_UNIT_SALES_INSTANCE:
      return {
        ...state,
        [UNIT_SALES_INSTANCES_KEY]: {
          ...state[UNIT_SALES_INSTANCES_KEY],
          [action.payload.id]: { ...defaultUnitSalesState },
        },
      };
    case actionTypes.UPDATE_UNIT_SALES_INSTANCE:
      return {
        ...state,
        [UNIT_SALES_INSTANCES_KEY]: {
          ...state[UNIT_SALES_INSTANCES_KEY],
          [action.payload.id]: {
            ...state[UNIT_SALES_INSTANCES_KEY][action.payload.id],
            ...action.payload.data,
          },
        },
      };
    case actionTypes.REMOVE_UNIT_SALES_INSTANCE: {
      const { [action.payload.id]: removed, ...remaining } =
        state[UNIT_SALES_INSTANCES_KEY];
      return {
        ...state,
        [UNIT_SALES_INSTANCES_KEY]: remaining,
      };
    }
    case actionTypes.SET_UNIT_SALES_INSTANCE_BY_MONTH: {
      const instance = state[UNIT_SALES_INSTANCES_KEY][action.id];
      const item = instance[action.itemKey];
      return {
        ...state,
        [UNIT_SALES_INSTANCES_KEY]: {
          ...state[UNIT_SALES_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...item,
              months: {
                ...item.months,
                [action.monthKey]: action.amount,
              },
            },
          },
        },
      };
    }
    case actionTypes.REPLACE_UNIT_SALES_INSTANCE_MONTHS: {
      const instance = state[UNIT_SALES_INSTANCES_KEY][action.id];
      return {
        ...state,
        [UNIT_SALES_INSTANCES_KEY]: {
          ...state[UNIT_SALES_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...instance[action.itemKey],
              months: action.payload,
            },
          },
        },
      };
    }
    case actionTypes.RESET_UNIT_SALES_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default unitSalesReducer;
